import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { CopyField } from "@/pages/mlo/Integrations";
import { ZapierTestCard } from "@/components/ZapierTestCard";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, KeyRound, Loader2 } from "lucide-react";

export const AdminMloIntegration = ({ c, webhookUrl, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    if (c.connected && !window.confirm(`Generate a new key for ${c.name || c.email}? Their current Zap stops working until the new key is pasted in.`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/admin/integrations/astoria/${c.mlo_id}/key`); setNewKey(data.key); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const toggle = async (v) => {
    try { await api.post(`/admin/integrations/astoria/${c.mlo_id}/toggle`, { enabled: v }); toast.success(v ? "Connection on" : "Connection off"); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const status = !c.connected ? "Not connected" : c.enabled ? "On" : "Off";
  return (
    <div className="border-b border-slate-100 py-3 last:border-0" data-testid={`admin-astoria-mlo-${c.mlo_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span><b>{c.name || c.email}</b> <span className="text-slate-500">{c.email}</span>{c.role === "admin" && <span className="ml-1 text-xs text-slate-400">(admin)</span>}</span>
        <span className="flex items-center gap-3">
          <span className="text-slate-600">{c.leads} leads</span>
          <Badge className={`border-0 ${c.connected && c.enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`} data-testid={`admin-astoria-status-${c.mlo_id}`}>{status}</Badge>
          <Button size="sm" variant="outline" onClick={() => setOpen(!open)} data-testid={`admin-astoria-setup-${c.mlo_id}`}>
            Set up {open ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />}
          </Button>
        </span>
      </div>
      {open && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
          <CopyField label="Webhook URL" value={webhookUrl} testId={`admin-astoria-webhook-${c.mlo_id}`} />
          {newKey ? (
            <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-900">Copy this key now and paste it into the X-Elite-Key header of {c.name || "this loan officer"}'s Zap. It won't be shown again.</p>
              <CopyField label="Integration key (X-Elite-Key header)" value={newKey} testId={`admin-astoria-newkey-${c.mlo_id}`} />
            </div>
          ) : c.connected && <p className="mt-3 text-sm text-slate-600">Integration key ending in <b>…{c.key_last4}</b> · {c.received_count || 0} deliveries{c.last_received_at ? ` · last ${new Date(c.last_received_at).toLocaleString()}` : ""}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <Button size="sm" onClick={generate} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid={`admin-astoria-genkey-${c.mlo_id}`}>
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <KeyRound className="mr-1.5 h-4 w-4" />}{c.connected ? "Regenerate key" : "Generate integration key"}
            </Button>
            {c.connected && <span className="flex items-center gap-2 text-sm text-slate-600">Receiving leads <Switch checked={c.enabled} onCheckedChange={toggle} data-testid={`admin-astoria-switch-${c.mlo_id}`} /></span>}
          </div>
          <div className="mt-4"><ZapierTestCard testIdPrefix={`admin-zapier-${c.mlo_id}`} /></div>
        </div>
      )}
    </div>
  );
};
